import { Component, OnInit, ViewEncapsulation } from '@angular/core';
import { FilterData, IdentificationData, OwnOrganisationsAndSites, TimezoneOption } from '../common/types/common';
import {
  ageGroups, colors, genders, OWN_ORGANISATIONS_AND_SITES, STATISTICS_FILTER_DATA, timeIntervals, toastrConfig, visitorTypes
} from '../app.config';
import {
  formatDateToHHmm,
  formatDateToISOString,
  initTimezones,
  isInOpeningHours,
  onOrganisationChange
} from '../common/utils';
import { HttpParams } from '@angular/common/http';
import { FormBuilder, FormGroup } from '@angular/forms';
import { StatisticsService } from '../common/services/statistics.service';
import { StatisticsDTO } from '../common/types/statistics';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { CommonService } from '../common/services/common.service';
import { SettingsDTO } from '../common/types/settings';
import { forkJoin } from 'rxjs';
import { SettingsService } from '../common/services/settings.service';

@Component({
  selector: 'app-statistics',
  templateUrl: './statistics.component.html',
  encapsulation: ViewEncapsulation.None
})
export class StatisticsComponent implements OnInit {
  isLoading: boolean;
  isTableLoading: boolean = false;
  stateOptions: any[] = [{ label: 'Visitors', value: 'visitors' },{ label: 'Visits', value: 'visits' }];
  stateValue: string = 'visitors';
  filterForm!: FormGroup;
  storedFilters: FilterData = null;
  charts: any[] = [];
  ownOrganisationsAndSites: OwnOrganisationsAndSites[];
  organisations: IdentificationData[];
  sites: any[];
  statisticsData: StatisticsDTO;
  params: HttpParams = null;
  tableData: any;
  first = 0;
  rows = 10;
  timezones: TimezoneOption[] = [];
  selectedTimezone: string = 'UTC';
  settingsData: SettingsDTO;

  constructor(private statisticsService: StatisticsService,
              private commonService: CommonService,
              private settingsServices: SettingsService,
              private fb: FormBuilder,
              private translate: TranslateService,
              private toastr: ToastrService) { }

  ngOnInit() {
    this.isLoading = true;
    this.setupTimezones();

    this.filterForm = this.fb.group({
      selectedOrganisation: [null],
      selectedSite: [null],
      selectedPeriodStart: [null],
      selectedPeriodEnd: [null],
      selectedTimeInterval: [this.timeIntervals[0]],
      selectedAgeGroup: [null],
      selectedGender: [null],
      selectedVisitorType: [null]
    });

    this.storedFilters = JSON.parse(localStorage.getItem(STATISTICS_FILTER_DATA));
    this.ownOrganisationsAndSites = JSON.parse(localStorage.getItem(OWN_ORGANISATIONS_AND_SITES));
    this.organisations = this.ownOrganisationsAndSites.map(item => item.organisation);
    const initialOrg = this.storedFilters?.selectedOrganisation || (this.organisations.length > 0 ? this.organisations[0] : null);

    this.sites = onOrganisationChange(this.ownOrganisationsAndSites, initialOrg.id);
    const initialSite = this.storedFilters ? this.storedFilters.selectedSite : this.sites[0];

    const initialTimeInterval = this.storedFilters?.selectedTimeInterval || this.timeIntervals[0];
    const initialPeriodEnd = this.storedFilters?.selectedPeriodEnd ? new Date(this.storedFilters.selectedPeriodEnd) : new Date();
    const initialPeriodStart = this.storedFilters?.selectedPeriodStart ? new Date(this.storedFilters.selectedPeriodStart) : new Date();

    this.filterForm.patchValue({
      selectedOrganisation: initialOrg,
      selectedSite: initialSite,
      selectedPeriodStart: initialPeriodStart,
      selectedPeriodEnd: initialPeriodEnd,
      selectedTimeInterval: initialTimeInterval,
      selectedAgeGroup: this.storedFilters?.selectedAgeGroup || null,
      selectedGender: this.storedFilters?.selectedGender || null,
      selectedVisitorType: this.storedFilters?.selectedVisitorType || null
    });

    this.updateStatistics();
  }

  setupTimezones() {
    const rawTimezones = initTimezones();
    const keys = rawTimezones.map(tz => tz.label);

    this.translate.get(keys).subscribe((translations: any) => {
      this.timezones = rawTimezones.map(tz => ({
        ...tz,
        label: translations[tz.label]
      }));
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const match = this.timezones.find(tz => tz.value === userTimezone);
      this.selectedTimezone = match ? match.value : 'UTC';
    });
  }

  onTimezoneChange(event: any) {
    const newTimezone = event.value;
    console.log('Az új időzóna:', event.value);
  }

  onStateChange(newValue: any) {
    this.stateValue = newValue;
    this.isLoading = true;
    this.updateStatistics();
  }

  compareObjects(o1: any, o2: any): boolean {
    return o1 && o2 ? o1.id === o2.id : o1 === o2;
  }

  onOrganisationChanged(selectedOrg: any) {
    if (!selectedOrg) return;
    this.sites = onOrganisationChange(this.ownOrganisationsAndSites, selectedOrg.id);
    this.filterForm.patchValue({ selectedSite: this.sites[0] });
  }

  next() {
    this.first = this.first + this.rows;
  }

  prev() {
    this.first = this.first - this.rows;
  }

  createExcelExport() {
    this.isTableLoading = true;

    this.statisticsService.exportStatistics(this.params).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        window.open(url);
        this.isTableLoading = false;
      },
      error: (error) => {
        this.toastr.error(error.error.message, "", toastrConfig);
        this.isTableLoading = false;
      }
    });
  }

  loadDataLazy(event: any) {
    // Ha az oldal még globális betöltés alatt van (pl. most fut az updateStatistics), ne hívogassuk feleslegesen
    if (this.isLoading) return;

    // Ha a first és a rows értéke nem változott meg az előzőhöz képest, ne küldjünk felesleges kérést
    if (this.first === event.first && this.rows === event.rows && this.tableData?.rows) {
      return;
    }

    this.isTableLoading = true;
    this.first = event.first;
    this.rows = event.rows;

    const formValues = this.filterForm.value;
    const page = Math.floor(event.first / event.rows);

    this.params = new HttpParams()
      .set('organisation', formValues.selectedOrganisation?.id || '')
      .set('site', formValues.selectedSite ? formValues.selectedSite.id : '')
      .set('periodStart', formValues.selectedPeriodStart ? formatDateToISOString(new Date(formValues.selectedPeriodStart)) : '')
      .set('periodEnd', formValues.selectedPeriodEnd ? formatDateToISOString(new Date(formValues.selectedPeriodEnd)) : '')
      .set('interval', formValues.selectedTimeInterval)
      .set('visitorType', formValues.selectedVisitorType || 'all')
      .set('page', page)
      .set('pageSize', event.rows);

    if (formValues.selectedAgeGroup)
      this.params = this.params.set('ageGroups', formValues.selectedAgeGroup);
    if (formValues.selectedGender)
      this.params = this.params.set('genders', formValues.selectedGender);

    this.statisticsService.getStatistics(this.params).subscribe({
      next: (data: StatisticsDTO) => {
        this.statisticsData = data;
        this.tableData = data.table;
        this.isTableLoading = false;
      },
      error: (error) => {
        this.toastr.error(error.error.message, "", toastrConfig);
        this.isTableLoading = false;
      }
    });
  }

  updateStatistics() {
    this.isLoading = true;
    this.charts = [];
    const formValues = this.filterForm.value;

    const startDate = formValues.selectedPeriodStart ? new Date(formValues.selectedPeriodStart) : new Date();
    const endDate = formValues.selectedPeriodEnd ? new Date(formValues.selectedPeriodEnd) : new Date();

    const settingsParams = new HttpParams()
      .set('organisation', formValues.selectedOrganisation?.id || '')
      .set('site', formValues.selectedSite ? formValues.selectedSite.id : '');

    this.params = new HttpParams()
      .set('organisation', formValues.selectedOrganisation?.id || '')
      .set('site', formValues.selectedSite ? formValues.selectedSite.id : '')
      .set('periodStart', formatDateToISOString(startDate))
      .set('periodEnd', formatDateToISOString(endDate))
      .set('interval', formValues.selectedTimeInterval)
      .set('visitorType', formValues.selectedVisitorType || 'all')
      .set('page', 0)
      .set('pageSize', this.rows);

    if (formValues.selectedAgeGroup)
      this.params = this.params.set('ageGroups', formValues.selectedAgeGroup);
    if (formValues.selectedGender)
      this.params = this.params.set('genders', formValues.selectedGender);

    const filterDataToSave: FilterData = {
      selectedOrganisation: formValues.selectedOrganisation,
      selectedSite: formValues.selectedSite,
      selectedPeriodStart: formatDateToISOString(startDate),
      selectedPeriodEnd: formatDateToISOString(endDate),
      selectedTimeInterval: formValues.selectedTimeInterval,
      selectedAgeGroup: formValues.selectedAgeGroup,
      selectedGender: formValues.selectedGender,
      selectedVisitorType: formValues.selectedVisitorType
    };
    localStorage.setItem(STATISTICS_FILTER_DATA, JSON.stringify(filterDataToSave));

    forkJoin({
      statistics: this.statisticsService.getStatistics(this.params),
      settings: this.settingsServices.getSettings(settingsParams)
    }).subscribe({
      next: (data: any) => {
        this.statisticsData = data.statistics;
        this.settingsData = data.settings[0];
        this.tableData = this.statisticsData.table;

        this.getTrafficOverTime();
        this.getAgeAndGenderDistributionChartConfig();
        this.getNewVsReturningVisitorsChartConfig();
        this.getGenderDistributionChartConfig();

        this.isLoading = false;
      },
      error: (error) => {
        this.toastr.error(error.error.message, "", toastrConfig);
        this.isLoading = false;
      }
    });
  }

  getTrafficOverTime() {
    const traffic = this.statisticsData.trafficOverTime.filter(item => isInOpeningHours(this.settingsData, item.start));
    const labels = traffic.map(item => item.start.substring(5, 10) + ' ' + item.start.substring(11, 16));
    const entriesData = traffic.map(item => item.entries);
    const exitsData = traffic.map(item => item.exits);
    const occupancyData = traffic.map(item => item.averageOccupancy);

    const data = {
      labels: labels,
      datasets: [
        {
          type: 'bar',
          label: this.translate.instant('STATISTICS.OCCUPANCY_INSIDE'),
          data: occupancyData,
          backgroundColor: colors.secondary,
          yAxisID: 'y1',
          order: 2
        },
        {
          type: 'line',
          label: this.translate.instant('STATISTICS.ENTRIES_IN'),
          data: entriesData,
          borderColor: colors.green,
          backgroundColor: colors.white,
          pointBorderColor: colors.green,
          pointBackgroundColor: colors.white,
          tension: 0.2,
          fill: false,
          yAxisID: 'y',
          order: 1
        },
        {
          type: 'line',
          label: this.translate.instant('STATISTICS.EXITS_OUT'),
          data: exitsData,
          borderColor: colors.red,
          backgroundColor: colors.white,
          pointBorderColor: colors.red,
          pointBackgroundColor: colors.white,
          tension: 0.2,
          fill: false,
          yAxisID: 'y',
          order: 1
        }
      ]
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        y: {
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: this.translate.instant('STATISTICS.ENTRIES_EXITS'),
          },
          grid: {
            drawOnChartArea: true
          }
        },
        y1: {
          type: 'linear',
          position: 'right',
          title: {
            display: true,
            text: this.translate.instant('STATISTICS.OCCUPANCY_INSIDE'),
          },
          grid: {
            drawOnChartArea: false
          }
        }
      }
    };

    this.charts.push({ type: 'bar', data: data, options: options });
  }

  getAgeAndGenderDistributionChartConfig() {
    const chartData = this.commonService.getAgeAndGenderDistributionChartConfig(this.statisticsData.ageGenderDistribution[this.stateValue]);
    this.charts.push({ type: chartData.type, data: chartData.data, options: chartData.options });
  }

  getNewVsReturningVisitorsChartConfig() {
    const chartData = this.commonService.getNewVsReturningVisitorsChartConfig(this.statisticsData.newVsReturning[this.stateValue]);
    this.charts.push({ data: chartData.data, legendData: chartData.legendData });
  }

  getGenderDistributionChartConfig() {
    const chartData = this.commonService.getGenderDistributionChartConfig(this.statisticsData.genderDistribution[this.stateValue]);
    this.charts.push({ type: chartData.type, data: chartData.data, options: chartData.options, legendData: chartData.legendData, totalVisitors: chartData.totalVisitors });
  }

  protected readonly timeIntervals = timeIntervals;
  protected readonly ageGroups = ageGroups;
  protected readonly genders = genders;
  protected readonly visitorTypes = visitorTypes;
  protected readonly colors = colors;
}
